/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  MutationAddBoardStarArgs,
  MutationRemoveBoardStarArgs,
  MutationAddSavedSearchArgs,
  MutationDeleteSavedSearchArgs,
  MutationUpdateUserLocaleArgs,
  MutationUpdateTeamifyVoluntaryDoneArgs,
  MutationDeleteOneTimeMessagesDismissedArgs,
  MutationPrepMemberForEmailHygieneArgs,
  MutationUnblockMemberProfileSyncArgs,
  MutationAcceptDeveloperTermsArgs,
  MutationUpdateMemberAvatarSourceArgs,
  MutationUploadMemberAvatarArgs,
  MutationAddCampaignArgs,
  MutationUpdateCampaignArgs,
  MutationChangeMemberEmailArgs,
  MutationResendVerificationEmailArgs,
  QueryCheckDomainClaimArgs,
  QueryMemberCardsArgs,
  QueryMemberSearchArgs,
  Member_Atlassian_Organization,
  MutationUpdateMemberActiveChannelArgs,
  MutationUpdateBoardStarArgs,
  MutationUpdateEmailForMemberLoginArgs,
  MutationUpdateMemberProfileArgs,
  MutationAddOneTimeMessagesDismissedArgs,
} from '../generated';
import Cookie from 'js-cookie';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import type { JSONObject, ResolverContext, TrelloRestResolver } from '../types';
import { token } from '@trello/session-cookie';
import { atlassianApiGateway } from '@trello/config';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { trelloFetch, fetch } from '@trello/fetch';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { getNetworkClient } from '../getNetworkClient';
import { featureFlagClient } from '@trello/feature-flag-client';
import { isQueryInfo } from '../isQueryInfo';

export const addBoardStar: TrelloRestResolver<MutationAddBoardStarArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/member/${args.memberId}/boardStars`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
        idBoard: args.boardId,
        pos: args.pos,
      }),
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const removeBoardStar: TrelloRestResolver<MutationRemoveBoardStarArgs> = async (
  obj,
  args,
  context,
) => {
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/member/${args.memberId}/boardStars/${args.boardStarId}`,
  );
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });

    throw await parseNetworkError(response);
  }

  return true;
};

export const updateBoardStar: TrelloRestResolver<MutationUpdateBoardStarArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(
      `/1/member/${args.memberId}/boardStars/${args.boardStarId}`,
    ),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
        pos: args.pos,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }

  const json = await response.json();

  return prepareDataForApolloCache(json, rootNode);
};

export const addSavedSearch: TrelloRestResolver<MutationAddSavedSearchArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/member/${args.memberId}/savedSearches`,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...args.savedSearch,
      token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const deleteSavedSearch: TrelloRestResolver<MutationDeleteSavedSearchArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/member/${args.memberId}/savedSearches/${args.savedSearchId}`,
  );
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const updateUserLocale: TrelloRestResolver<MutationUpdateUserLocaleArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();

  searchParams.set('prefs/locale', args.locale);
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const response = await fetch(
    networkClient.getUrl(`/1/members/${args.memberId}`),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: searchParams,
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const updateTeamifyVoluntaryDone: TrelloRestResolver<MutationUpdateTeamifyVoluntaryDoneArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/members/${args.memberId}/teamify/voluntaryDone`,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: searchParams,
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};

export const prepMemberForEmailHygiene: TrelloRestResolver<MutationPrepMemberForEmailHygieneArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();

  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  if (args.dismissMessage) {
    // Perform the PUT
    const setPrimaryResponse = await fetch(
      networkClient.getUrl(
        `/1/member/${args.memberId}/logins/${args.primaryLoginId}/primary?peh=true`,
      ),
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'X-Trello-Client-Version': context.clientAwareness.version,
        },
        body: searchParams.toString(),
      },
    );

    if (!setPrimaryResponse.ok) {
      // Sometimes the server sends us just the string as the error, however
      // sometimes the server also sends a JSON error with the key 'message'
      // as the error
      let message = await setPrimaryResponse.text(); // Parse it as text
      let data = null;

      try {
        data = JSON.parse(message); // Try to parse it as json
        message = data.message;
        // eslint-disable-next-line no-empty
      } catch {}

      throw new Error(message);
    }
  } else {
    const networkClient = getNetworkClient();
    // Perform the PUT
    const setPrimaryResponse = await fetch(
      networkClient.getUrl(
        `/1/member/${args.memberId}/logins/${args.primaryLoginId}/primary`,
      ),
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'X-Trello-Client-Version': context.clientAwareness.version,
        },
        body: searchParams.toString(),
      },
    );

    if (!setPrimaryResponse.ok) {
      throw new Error(
        `Invalid response status ${setPrimaryResponse.status} from PUT ${setPrimaryResponse.url}`,
      );
    }

    // Delete all the other logins
    for (const idLogin of args.removeLoginIds) {
      const removeEmailResponse = await fetch(
        networkClient.getUrl(`/1/member/${args.memberId}/logins/${idLogin}`),
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            'X-Trello-Client-Version': context.clientAwareness.version,
          },
          body: searchParams.toString(),
        },
      );

      if (!removeEmailResponse.ok) {
        throw new Error(
          `Invalid response status ${removeEmailResponse.status} from DELETE ${removeEmailResponse.url}`,
        );
      }
    }
  }

  // Re-fetch the Member so that the return will correctly update the apollo cache
  const response = await trelloFetch(
    networkClient.getUrl(`/1/member/${args.memberId}?logins=true`),
    undefined,
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'prepMemberForEmailHygiene',
        operationType: 'mutation',
        operationName: context.operationName,
      },
    },
  );
  if (!response.ok) {
    throw new Error(
      `Invalid response status ${response.status} from GET ${response.url}`,
    );
  }
  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const unblockMemberProfileSync: TrelloRestResolver<MutationUnblockMemberProfileSyncArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const postResponse = await fetch(
    networkClient.getUrl(
      `/1/member/${args.memberId}/atlassianAccount/migrationComplete`,
    ),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: searchParams.toString(),
    },
  );

  if (!postResponse.ok) {
    // Sometimes the server sends us just the string as the error, however
    // sometimes the server also sends a JSON error with the key 'message'
    // as the error
    let message = await postResponse.text(); // Parse it as text
    let data = null;

    try {
      data = JSON.parse(message); // Try to parse it as json
      message = data.message;
      // eslint-disable-next-line no-empty
    } catch {}

    throw new Error(message);
  }

  // Re-fetch the Member so that the return will correctly update the apollo cache
  const response = await trelloFetch(
    networkClient.getUrl(
      `/1/member/${args.memberId}?fields=aaBlockSyncUntil,oneTimeMessagesDismissed,requiresAaOnboarding`,
    ),
    undefined,
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'unblockMemberProfileSync',
        operationType: 'query',
        operationName: context.operationName,
      },
    },
  );
  if (!response.ok) {
    throw new Error(
      `Invalid response status ${response.status} from GET ${response.url}`,
    );
  }
  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

// Internal endpoint for development and trelloinc members
export const deleteOneTimeMessagesDismissed: TrelloRestResolver<MutationDeleteOneTimeMessagesDismissedArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();

  searchParams.set('value', args.message);
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const response = await fetch(
    networkClient.getUrl(
      `/1/members/${args.memberId}/oneTimeMessagesDismissed`,
    ),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: searchParams,
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const addOneTimeMessagesDismissed: TrelloRestResolver<MutationAddOneTimeMessagesDismissedArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();

  searchParams.set('value', args.messageId);
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const response = await fetch(
    networkClient.getUrl(
      `/1/members/${args.memberId}/oneTimeMessagesDismissed`,
    ),
    {
      method: 'POST',
      credentials: 'include',
      body: searchParams,
      headers: {
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
    },
  );

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const memberAgreementsResolver: TrelloRestResolver<object> = async (
  member: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/${member.id}/agreements`);

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Member.agreements',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, rootNode, 'Member') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const memberAtlassianOrganizationsResolver: TrelloRestResolver<object> = async (
  member: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/members/${member.id}/atlassianOrganizations`,
  );

  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'Member.atlassianOrganizations',
      operationName: context.operationName,
    },
  });

  if (response.ok) {
    model = await response.json();
  } else if (response.status === 404) {
    return null;
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  // attempt to populate SSO URLs for any administered Orgs without a value
  await Promise.all(
    model.map(async (org: Member_Atlassian_Organization) => {
      if (org.isIdentityAdmin) {
        try {
          const flexAuthSsoUrl = await atlassianOrganizationFlexAuthSsoUrl(
            org.id,
            context,
          );
          if (flexAuthSsoUrl) {
            org.ssoUrl = flexAuthSsoUrl;
          }
        } catch (e) {
          // If the request to fetch the Flex Auth SAML configuration fails,
          // we don't want to re-throw that error from here, because there
          // could be successful requests for other AtlOrgs. But we do want
          // to surface that error in the UI. So we set flexAuthError and use
          // that in the component to show an error message.
          org.flexAuthError = true;
        }
      }
    }),
  );
  return model ? prepareDataForApolloCache(model, rootNode, 'Member') : model;
};

/**
 * Returns the SSO URL for the Atlassian Org *if* it is FlexAuth
 * Atlassian Orgs are migrating from legacy to FlexAuth. This changes how SSO
 * is modeled. This API only contains the SSO URL for FlexAuth organizations
 * Trello can only obtain SSO URLs for non-FlexAuth organizations. To bridge
 * this gap we will check for an SSO URL from this API if the record from
 * Trello's API doesn't have a value for ssoUrl.
 */
export async function atlassianOrganizationFlexAuthSsoUrl(
  orgId: string,
  context: ResolverContext,
) {
  const apiUrl = `${atlassianApiGateway}gateway/api/admin/private/org/${orgId}/saml-configuration`;

  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'Member.atlassianOrganizations',
      operationName: context.operationName,
    },
  });

  // Two status codes are expected errors. Ignore them.
  // 404 returned when OrgId isn't found or Org isn't FlexAuth.
  // 402 returned when Org isn't licensed for Access.
  if (response.status === 404 || response.status === 402) {
    return null;
  } else if (response.ok) {
    const { samlConfiguration } = await response.json();

    // If status code 200 is returned, but the ssoUrl is empty,
    // throw an error, so the component knows to show an error flag.
    if (!samlConfiguration?.ssoUrl) {
      throw new Error(
        `An error occurred while fetching Org SAML Config. ` +
          `(status: ${response.status}, statusText: ${response.statusText}, ` +
          `orgId: ${orgId}, message: SSO Url was empty.`,
      );
    }

    return samlConfiguration?.ssoUrl;
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
      ownershipArea: 'trello-aaaa',
    });
    throw await parseNetworkError(response);
  }
}

export const acceptDeveloperTerms: TrelloRestResolver<MutationAcceptDeveloperTermsArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/${args.memberId}/agreements`);

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        agreementType: 'developer-terms',
        token: context.token || token,
      }),
    });

    if (response.ok) {
      model = await response.json();
    } else {
      throw new Error(
        `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
      );
    }

    return model ? prepareDataForApolloCache(model, rootNode) : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const updateMemberAvatarSource: TrelloRestResolver<MutationUpdateMemberAvatarSourceArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const body = new URLSearchParams();
  body.set('avatarSource', args.avatarSource);
  body.set('token', context.token || token || '');

  const networkClient = getNetworkClient();

  const response = await fetch(networkClient.getUrl(`/1/members/me`), {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body,
  });

  const member = await response.json();

  return prepareDataForApolloCache(member, rootNode);
};

export const updateMemberProfile: TrelloRestResolver<MutationUpdateMemberProfileArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const operationNameInUrl = featureFlagClient.get(
    'fep.operation-name-in-url',
    false,
  );
  const queryString = operationNameInUrl
    ? `?operationName=${context.operationName}`
    : '';
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/me${queryString}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...args.profile,
      token: context.token || token || '',
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const uploadMemberAvatar: TrelloRestResolver<MutationUploadMemberAvatarArgs> = async (
  obj,
  { file, uploadProgressCallback },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const formData = new FormData();
  formData.set('file', file);
  formData.set('token', context.token || token || '');

  // We need to use XHR in order to track upload progress
  const request = new Promise<JSONObject>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;

    if (uploadProgressCallback) {
      const upload = xhr.upload;

      upload.addEventListener('progress', (e) => {
        // 64kb is the minimum threshold
        if (!e.lengthComputable || e.total < 1024 * 64) {
          return;
        }

        uploadProgressCallback(Math.round((100 * e.loaded) / e.total));
      });

      upload.addEventListener('load', () => {
        uploadProgressCallback(100);
      });
    }

    const networkClient = getNetworkClient();
    xhr.open('POST', networkClient.getUrl(`/1/member/me/avatar`));
    xhr.onload = () => {
      const response = JSON.parse(xhr.response);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(response);
      } else {
        reject(response);
      }
    };
    xhr.onerror = () => {
      reject({ status: xhr.status, statusText: xhr.statusText });
    };

    xhr.send(formData);
  });

  try {
    const member = await request;
    return prepareDataForApolloCache(member, rootNode);
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const removeMyAvatar: TrelloRestResolver<object> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/members/me/avatar');
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token || '',
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const updateEmailForMemberLogin: TrelloRestResolver<MutationUpdateEmailForMemberLoginArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const operationNameInUrl = featureFlagClient.get(
    'fep.operation-name-in-url',
    false,
  );
  const queryString = operationNameInUrl
    ? `?operationName=${context.operationName}`
    : '';
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/members/me/logins/${args.loginId}${queryString}`,
  );
  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      email: args.email,
      token: context.token || token || '',
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const addCampaign: TrelloRestResolver<MutationAddCampaignArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/members/me/campaigns');
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...(args || {}),
      token: context.token || token,
    }),
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};

export const updateCampaign: TrelloRestResolver<MutationUpdateCampaignArgs> = async (
  obj,
  args,
  context,
) => {
  const { campaignId, ...rest } = args;
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/me/campaigns/${campaignId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...(rest || {}),
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }
};

export const changeMemberEmail: TrelloRestResolver<MutationChangeMemberEmailArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;
  let response = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/members/me/logins');

  const { email, loginId, primary } = args;

  try {
    response = await fetch(`${apiUrl}/${loginId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        email,
        primary,
        token: context.token || token,
      }),
    });

    if (response.ok) {
      model = await response.json();

      if (args.dismissMessage) {
        const networkClient = getNetworkClient();
        const dismissResponse = await fetch(
          networkClient.getUrl(`/1/member/me/oneTimeMessagesDismissed`),
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Trello-Client-Version': context.clientAwareness.version,
            },
            body: JSON.stringify({
              token: context.token || token,
              value: args.dismissMessage,
            }),
          },
        );

        if (!dismissResponse.ok) {
          throw new Error(
            `Invalid response status ${dismissResponse.status} from POST ${dismissResponse.url}`,
          );
        }
      }
    } else {
      throw new Error(
        `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
      );
    }

    return model ? prepareDataForApolloCache(model, rootNode) : model;
  } catch (err) {
    if (response) {
      // Sometimes the server sends us just the string as the error, however
      // sometimes the server also sends a JSON error with the key 'message'
      // as the error
      let message = await response.text(); // Parse it as text
      let data = null;

      try {
        data = JSON.parse(message); // Try to parse it as json
        message = data.message;
        // eslint-disable-next-line no-empty
      } catch {}

      throw new Error(message);
    } else {
      throw new Error('Unknown error');
    }
  }
};

export const resendVerificationEmail: TrelloRestResolver<MutationResendVerificationEmailArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/resendValidate');

  const { email } = args;

  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      email,
      token: Cookie.get(`token${window.location.port ? '3000' : ''}`) || '',
    }),
  });

  if (response.ok) {
    model = await response.json();

    if (model.badEmail) {
      throw new Error('bad email');
    } else if (model.alreadyConfirmed) {
      throw new Error('already confirmed');
    }
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL mutation. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }

  return model ? prepareDataForApolloCache(model, rootNode) : model;
};

export const updateMemberActiveChannel: TrelloRestResolver<MutationUpdateMemberActiveChannelArgs> = async (
  parent,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const searchParams = new URLSearchParams();
  searchParams.set('value', args.channel);
  searchParams.set('token', context.token || token || '');

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/members/${args.memberId}/channels/active`,
  );

  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': context.clientAwareness.version,
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: searchParams,
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(
      { id: args.memberId, channels: model },
      rootNode,
    );
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};

export const memberAtlassianAccountsResolver: TrelloRestResolver<object> = async (
  parent,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/members/me/logins/atlassianAccounts');

  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'atlassianAccounts',
      operationName: context.operationName,
    },
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(model, rootNode);
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};

// Very specific resolver to use on /members/id/cards
export const memberCardsResolver: TrelloRestResolver<QueryMemberCardsArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { id, limit, before, modifiedSince, sort } = args;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/members/${id}/cards`);

  const params = new URLSearchParams();
  params.set('filter', 'visible');
  params.set('stickers', 'true');
  params.set('attachments', 'true');
  params.set('members', 'true');

  if (limit) {
    params.set('limit', limit.toString());
  }
  if (before) {
    params.set('before', before);
  }
  if (modifiedSince) {
    params.set('modifiedSince', modifiedSince.toString());
  }
  if (sort) {
    params.set('sort', sort);
  }

  const response = await trelloFetch(`${apiUrl}?${params}`, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'memberCards',
      operationName: context.operationName,
    },
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(model, rootNode);
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};

export const memberSearchResolver: TrelloRestResolver<QueryMemberSearchArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/search/members`);

  const params = new URLSearchParams();

  Object.entries(args).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, value.toString());
    }
  });

  const response = await trelloFetch(`${apiUrl}?${params}`, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'memberSearch',
      operationName: context.operationName,
    },
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};

export const checkDomainClaimResolver: TrelloRestResolver<QueryCheckDomainClaimArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/members/me/logins/checkDomainClaim');
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      email: args.email,
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(await response.json(), rootNode);
};
